import { useState } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { useAuthMember, useVerifyMember } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { useResendVerificationCode } from './hooks/useResendVerificationCode'
import { onAuthenticate } from './utils/onAuthenticate'

export const VerifyForm = ({
  memberId: memberIdProp,
}: {
  memberId?: string
}) => {
  const { $t } = useI18n()
  const { update: updateRefreshToken } = useRefreshToken()
  const { redirect } = useRouter()

  const { data: member, isGuest } = useAuthMember()

  const {
    onResend,
    isResending: resendIsLoading,
    codeResent,
  } = useResendVerificationCode()

  const [isLoading, setLoading] = useState(false)
  const { mutateAsync: verify } = useVerifyMember({
    useMutationOptions: {
      onMutate: () => {
        setLoading(true)
      },
      onError: () => {
        setLoading(false)
      },
    },
  })

  const memberId = memberIdProp ?? member?.id

  const onSubmit = async data => {
    return verify(
      {
        input: {
          memberId,
          verificationCode: data?.verificationCode,
        },
      },
      {
        onSuccess: async authToken => {
          const newAuthToken = await updateRefreshToken(authToken)
          const { refreshToken, accessToken } = newAuthToken || {}
          if (accessToken || refreshToken) {
            onAuthenticate()
            redirect('/')
            // Do not set loading to false if we're reloading the page right after.
            return
          }
          setLoading(false)
        },
      },
    )
  }

  return (
    <Form onSubmit={onSubmit}>
      {methods => (
        <Form.Layout>
          <p>
            {isGuest ? (
              <T
                defaultMessage="We have sent you a verification code to your email. Please enter the code here."
                id="Auth.VerifyAccount.EmailNotice.Guest"
              />
            ) : (
              <T
                defaultMessage="We have sent a verification code to your email at {email}. Please enter the code here."
                id="Auth.VerifyAccount.EmailNotice.Member"
                values={{
                  email: <strong>{member.email}</strong>,
                }}
              />
            )}
          </p>
          <Form.Input
            name="verificationCode"
            label="Verification code"
            placeholder="5-digit alpha-numeric code"
            autoComplete="off"
            autoFocus
            disabled={isLoading}
            onChange={e => {
              if (e.target.value.length === 5) {
                methods.submitForm({
                  verificationCode: e.target.value,
                })
              }
            }}
            validation={{
              required: $t({
                defaultMessage: 'Verification code is required.',
                id: 'Auth.VerifyAccount.CodeRequired',
              }),
            }}
            helperText={$t({
              defaultMessage:
                'If you have not received the code, use the "Resend" button below to get a new code.',
              id: 'Auth.VerifyAccount.ResendHelperText',
            })}
          />

          <Form.Actions>
            <Button type="submit" size="lg" loading={isLoading}>
              <T defaultMessage="Verify" id="Auth.Verify.Actions.Submit" />
            </Button>
            {!codeResent && (
              <Button
                size="lg"
                disabled={isLoading || resendIsLoading}
                variant="outline"
                onClick={onResend}
              >
                <T defaultMessage="Resend" id="Auth.Verify.Actions.Resend" />
              </Button>
            )}
          </Form.Actions>
        </Form.Layout>
      )}
    </Form>
  )
}
